/**
 * Kennzahlen der Übersicht (Agentur) aus Kooperationsmonaten, Zusatzpositionen und Rechnungen.
 * Umsatzquelle sind die Kooperationsmonate, nicht die Rechnungszeilen (wie im bisherigen Portal, KZ-01).
 * Rein und ohne Datenbank; die Web-App liefert die Zeilen, dieselbe Rechnung dient Tests und Betrieb.
 *
 * Begriffe
 *   Budget        Betrag des Monats (Talent + Agentur, netto), bei „Nur Provision“ 0
 *   Zusatz        Zusatzpositionen des Monats (z. B. Nutzungsrechte); provisionsfrei = durchlaufend
 *   Agenturanteil Provision auf Budget und provisionspflichtigen Zusatz; bei „Nur Provision“ der Nettobetrag der wirksamen
 *                 Agenturrechnung des Monats, sonst der erwartete Wert; bei „Fest + Provision“ kommt die erwartete Provision hinzu
 *   Gebühr        Agenturgebühr zusätzlich: Prozent je Monat auf das Budget, Festbetrag nur im ersten Monat mit Betrag (KZ-07)
 *   Management    Agenturanteil + Gebühr (KZ-09)
 *   verbucht      Monat von Hand gebucht oder Rechnung an die Marke bezahlt (KA-05), sonst vorläufig
 *   Ist           Liegt eine freigegebene Rechnung des Creators für den Monat vor, ersetzt sie die Prognose des Creator-Anteils
 *                 (AD-019); durchlaufende Posten, die der Creator weglässt, werden nie zum Agenturanteil
 *   Gesamtvolumen Budget + Zusatz + Gebühr, netto (vor Aufteilung, ohne Umsatzsteuer)
 *   Buchung       Manuelle Buchungen zählen immer; nur eine Buchung, die ausdrücklich einen Kooperationsmonat abbildet
 *                 (period_id gesetzt) und deren Monat selbst Umsatz ausweist, bleibt außen vor (KZ-13)
 *   Storno        Ein stornierter Monat zählt nie; ein Monat mit Ausfallhonorar oder Minderung zählt mit diesem Betrag, auch wenn
 *                 die Kooperation storniert ist (Nachprüfung 0060)
 *   Vermittler    Vermittlerprovision je Monat: Aufwand der Agentur, eigene Größe neben dem Agenturumsatz (referralCents)
 *   Abzug         Agenturvergütung der Marke (AE-Provision): die Marke zahlt weniger; trägt die Aufteilung oder die Agentur
 *   Staffel       gestaffelte Provision je Monat (kooperationen.ts)
 */
import { addMonths, monthRange } from "./periods.js";
import { resolveCommissionRate } from "./split.js";
import { brandDiscountCents, brandDiscountFromRow, commissionCents, dealRevenueStarted, tiersFromJson, type BrandDiscount, type CommissionTier } from "./kooperationen.js";
import { dealBpForMonth, type DealPostTermFields } from "./post-term.js";
import { referralFromRow, type ReferralTerms } from "./referral.js";
import { effectiveRoute, type InvoiceRoute } from "./settlement.js";
import type { Cents, IsoDate, MonthKey } from "./types.js";

// Fachregeln der Kooperationen (Staffel, Umsatzbeginn, Monatsausgang, Abzug der Marke, Exklusivität, Warenwert)
export * from "./kooperationen.js";

export type OverviewFee = { mode: "none" } | { mode: "percent"; percent: number } | { mode: "fixed"; cents: Cents };

export interface OverviewDeal {
  id: string;
  talentId: string;
  brandId: string;
  model: string;
  status: string;
  currency: string;
  /** Satz der Kooperation in Prozent, sonst null */
  commissionPercent: number | null;
  /** Gebühr der Kooperation; null = Agentur-Standard */
  fee: OverviewFee | null;
  platforms?: readonly string[];
  archived?: boolean;
  /** Gesamtbudget der Kooperation; nur für die Pipeline, solange noch keine Monate angelegt sind */
  budgetCents?: Cents | null;
  /** Wirksamer Abrechnungsweg (Kooperation → Marke → Agentur); ohne Angabe über die Agentur */
  route?: InvoiceRoute;
  /**
   * Umsatzbeginn nach der Einstellung der Agentur (0047): false = zählt noch nicht (z. B. Vertrag noch nicht unterschrieben),
   * dann nur verbuchte Monate. Ohne Angabe zählt die Kooperation wie bisher. Regel: dealRevenueStarted (kooperationen.ts).
   */
  revenueStarted?: boolean;
  /**
   * Die Agentur zählt Umsatz ausdrücklich erst ab dem unterschriebenen Vertrag (revenue_start = contract_signed): dann zählen
   * vorher wirklich nur verbuchte Monate. In den anderen Einstellungen gilt eine festgeschriebene Rechnung als Beginn (K-138).
   */
  revenueByContract?: boolean;
  /** Gestaffelte Provision (festgeschrieben an der Kooperation); geht dem Satz vor */
  commissionTiers?: readonly CommissionTier[] | null;
  /** Vermittlerprovision (Partneragentur, Scout) */
  referral?: ReferralTerms | null;
  /** Agenturvergütung der Marke (AE-Provision, Agenturrabatt) */
  brandDiscount?: BrandDiscount | null;
  /** Beim Austritt des Creators umgestellt (0074): Satz je Abrechnungsmonat nach der Nachlaufregel */
  postTerm?: Omit<DealPostTermFields, "commission_bp"> | null;
}
export interface OverviewPeriod {
  id: string;
  dealId: string;
  month: MonthKey;
  amountCents: Cents | null;
  commissionForecastCents: Cents | null;
  bookedAt: string | null;
  invoiceRequestedAt: string | null;
  /** Monat storniert (ohne Ausfallhonorar): zählt nie */
  cancelled?: boolean;
  /** Ausfallhonorar oder Minderung: der Monat zählt mit seinem Betrag, auch wenn die Kooperation storniert ist */
  cancellationFee?: boolean;
  /**
   * Im bisherigen System abgerechnet bzw. bezahlt (deal_periods.imported_state, 0064/0095): der Monat ist an die Marke
   * abgerechnet, auch ohne Rechnung im Portal (PRAXIS-R3-1)
   */
  importedState?: ImportedState | null;
}
/** Stand eines übernommenen Monats: abgerechnet, Marke hat bezahlt (Creator offen), vollständig bezahlt */
export type ImportedState = "invoiced" | "brand_paid" | "paid";
export interface OverviewExtra {
  dealId: string;
  periodId: string | null;
  amountCents: Cents;
  commissionable: boolean;
  /** Angelegt am; nach der Rechnung des Creators angelegte Positionen (Nachkauf) sind von ihr nicht gedeckt */
  createdAt?: string | null;
}
export interface OverviewInvoice {
  id: string;
  kind: "talent_to_agency" | "agency_to_brand" | "talent_to_brand" | "agency_to_talent" | "agency_to_talent";
  dealId: string | null;
  periodId: string | null;
  talentId: string | null;
  status: string;
  currency: string;
  grossCents: Cents | null;
  revenueMonth: MonthKey | null;
  netCents?: Cents | null;
  /** Fälligkeit (wirksam) für die Altersstruktur offener Rechnungen */
  dueDate?: IsoDate | null;
  /** Beim Einreichen oder Versenden eingefrorener Bruttobetrag in der Agenturwährung */
  amountOrgCents?: Cents | null;
  issueDate?: IsoDate | null;
  /** Sammelrechnung an die Marke (0047): weitere Kooperationsmonate, die diese Rechnung abrechnet */
  periodIds?: readonly string[];
  /** Angelegt am (für Nachkäufe nach der Rechnung des Creators) */
  createdAt?: string | null;
  /** Summe der Zahlungen (invoice_payments, auch Teilzahlungen); offene Posten zählen nur den Rest (PRAXIS-5) */
  paidCents?: Cents | null;
  /** Als uneinbringlich ausgebucht (Forderungsausfall): steht auf „bezahlt“, ist aber kein Geldeingang (GELD-5) */
  badDebt?: boolean;
  /** Versendet, aber nicht zugegangen (Rückläufer bzw. unterwegs): keine Frist, kein Verzug (GELD-R3-7, wie invoice-state) */
  awaitsDelivery?: boolean;
  /** Stornorechnung zu dieser Rechnung: rechnet selbst keinen Monat ab (Praxistest K-138) */
  creditFor?: string | null;
}

/** Rechnungszeile aus der Datenbank (Übersicht, Kennzahlen, Stammdaten): eine Umwandlung für alle Seiten. */
export interface InvoiceRowForOverview {
  id: string; kind: string; deal_id: string | null; period_id: string | null; talent_id: string | null; status: string; currency: string;
  gross_cents: number | null; net_cents?: number | null; revenue_month: string | null; effective_due_date?: string | null; amount_org_cents?: number | null;
  issue_date?: string | null; created_at?: string | null; paid_cents?: number | null; bad_debt_at?: string | null;
  /** Versand und Zugang (0055/0085); fehlen sie in der Abfrage, gilt die Rechnung als zugegangen */
  sent_at?: string | null; delivered_at?: string | null; credit_for?: string | null;
}

/**
 * Rechnung an die Marke versendet, aber nicht zugegangen (Rückläufer der Rechnungsmail oder noch unterwegs): ohne Zugang läuft
 * keine Frist, es gibt keinen Verzug (GELD-R3-7, dieselbe Regel wie awaitsDelivery in invoice-state). delivered_at muss dafür
 * ausdrücklich null sein; fehlt die Spalte in der Abfrage (undefined), gilt die Rechnung als zugegangen.
 */
export function rowAwaitsDelivery(i: { kind: string; status: string; sent_at?: string | null; delivered_at?: string | null; credit_for?: string | null }): boolean {
  return i.status === "sent" && (i.kind === "agency_to_brand" || i.kind === "talent_to_brand") && !i.credit_for && i.sent_at != null && i.delivered_at === null;
}
export function overviewInvoiceFromRow(i: InvoiceRowForOverview, periodIds?: readonly string[]): OverviewInvoice {
  return {
    id: i.id, kind: i.kind as OverviewInvoice["kind"], dealId: i.deal_id, periodId: i.period_id, talentId: i.talent_id, status: i.status, currency: i.currency,
    grossCents: i.gross_cents, netCents: i.net_cents ?? null, revenueMonth: i.revenue_month ? i.revenue_month.slice(0, 7) : null, dueDate: i.effective_due_date ?? null,
    amountOrgCents: i.amount_org_cents ?? null, issueDate: i.issue_date ?? null, createdAt: i.created_at ?? null, paidCents: i.paid_cents ?? 0,
    ...(i.bad_debt_at ? { badDebt: true } : {}), ...(periodIds?.length ? { periodIds } : {}), ...(rowAwaitsDelivery(i) ? { awaitsDelivery: true } : {}),
    ...(i.credit_for ? { creditFor: i.credit_for } : {}),
  };
}

// ── Zeilen aus der Datenbank (eine Umwandlung für Übersicht, Kennzahlen und Wächter) ─────────────────────────────

/** Ausgänge, bei denen der Monat mit einem Betrag abrechenbar bleibt (Ausfallhonorar, Minderung) */
export const OUTCOMES_WITH_FEE: ReadonlySet<string> = new Set(["cancellation_fee", "not_accepted_fee", "not_accepted_reduced"]);

export interface DealRowForOverview {
  id: string; talent_id: string; brand_id: string; model: string; status: string; currency: string; commission_bp: number | null;
  platforms?: readonly string[] | null; archived_at?: string | null; budget_cents?: number | null; invoice_route?: string | null;
  commission_tiers?: unknown; brand_discount_bp?: number | null; brand_discount_bearer?: string | null;
  post_term?: unknown; post_term_from?: string | null; post_term_until?: string | null;
}
/** Kooperation für den Rechenkern: Weg (Kooperation → Marke → Agentur), Umsatzbeginn, Staffel, Vermittler, Abzug der Marke. */
export function overviewDealFromRow(d: DealRowForOverview, ctx: {
  fee: OverviewFee | null;
  terms?: { referral_mode?: string | null; referral_bp?: number | null; referral_cents?: number | null; referral_base?: string | null; referral_recipient?: string | null } | null;
  brandRoute?: string | null; orgRoute?: string | null; revenueStart?: string | null; signed?: boolean;
  /** first_event (0109): schon eine festgeschriebene Rechnung oder ein Beitrag „live“ (dealRevenueActivity) */
  activity?: boolean;
}): OverviewDeal {
  return {
    id: d.id, talentId: d.talent_id, brandId: d.brand_id, model: d.model, status: d.status, currency: d.currency,
    commissionPercent: d.commission_bp == null ? null : d.commission_bp / 100, fee: ctx.fee, platforms: d.platforms ?? [], archived: !!d.archived_at,
    budgetCents: d.budget_cents ?? null,
    route: effectiveRoute(d.invoice_route, ctx.brandRoute, ctx.orgRoute),
    revenueStarted: dealRevenueStarted(d.status, { mode: ctx.revenueStart, signed: ctx.signed, activity: ctx.activity }),
    ...(ctx.revenueStart === "contract_signed" ? { revenueByContract: true } : {}),
    commissionTiers: tiersFromJson(d.commission_tiers),
    referral: referralFromRow(ctx.terms),
    brandDiscount: brandDiscountFromRow(d),
    postTerm: d.post_term && d.post_term_from ? { post_term: d.post_term, post_term_from: d.post_term_from, post_term_until: d.post_term_until ?? null } : null,
  };
}
export interface PeriodRowForOverview {
  id: string; deal_id: string; month: string; amount_cents: number | null; commission_forecast_cents: number | null; booked_at: string | null;
  invoice_requested_at: string | null; status?: string | null; outcome?: string | null; cancellation_fee_cents?: number | null;
  imported_state?: string | null;
}
export function overviewPeriodFromRow(p: PeriodRowForOverview): OverviewPeriod {
  const fee = (!!p.outcome && OUTCOMES_WITH_FEE.has(p.outcome)) || (p.cancellation_fee_cents != null && p.status !== "cancelled");
  return {
    id: p.id, dealId: p.deal_id, month: p.month.slice(0, 7), amountCents: p.amount_cents, commissionForecastCents: p.commission_forecast_cents,
    bookedAt: p.booked_at, invoiceRequestedAt: p.invoice_requested_at, cancelled: p.status === "cancelled", ...(fee ? { cancellationFee: true } : {}),
    ...(isImportedState(p.imported_state) ? { importedState: p.imported_state } : {}),
  };
}

export function isImportedState(v: unknown): v is ImportedState {
  return v === "invoiced" || v === "brand_paid" || v === "paid";
}

/** Monate, die eine Rechnung abrechnet: der eigene Monat und bei Sammelrechnungen die verknüpften. */
export function invoicePeriodIds(i: Pick<OverviewInvoice, "periodId" | "periodIds">): string[] {
  const out = i.periodId ? [i.periodId] : [];
  for (const p of i.periodIds ?? []) if (!out.includes(p)) out.push(p);
  return out;
}
/** Manuelle Buchung (KZ-13): zählt nur mit dem Agenturanteil; gebucht = verbucht, geplant = vorläufig. */
export interface OverviewBooking {
  id: string;
  dealId: string | null;
  /** Gesetzt = die Buchung bildet genau diesen Kooperationsmonat ab */
  periodId?: string | null;
  talentId: string | null;
  brandId: string | null;
  kind: string;
  bookedOn: IsoDate;
  amountCents: Cents;
  currency: string;
  agencyShareCents: Cents | null;
  status: "planned" | "booked" | "cancelled" | string;
  /** Beim Buchen eingefrorener Kurs in die Agenturwährung (KZ-14) */
  fxRateToOrg?: number | null;
  amountOrgCents?: Cents | null;
}
export interface OverviewInput {
  today: IsoDate;
  from: MonthKey;
  to: MonthKey;
  talentId?: string | null;
  currency: string;
  agencyCommissionPercent: number;
  talentCommissionPercent: Readonly<Record<string, number | null>>;
  agencyFee: OverviewFee;
  deals: readonly OverviewDeal[];
  periods: readonly OverviewPeriod[];
  extras: readonly OverviewExtra[];
  invoices: readonly OverviewInvoice[];
  bookings?: readonly OverviewBooking[];
  /** Agenturwährung: Festgebühr der Agentur und eingefrorene Beträge sind in dieser Währung (Standard: currency) */
  orgCurrency?: string;
  /** Umrechnung in die gewählte Währung; null = kein Kurs (Betrag bleibt außen vor und wird gezählt) */
  convert?: (cents: Cents, from: string, to: string, month: MonthKey) => Cents | null;
}

export interface Occurrence {
  /** Kooperationsmonat oder manuelle Buchung */
  source: "deal" | "booking";
  dealId: string;
  periodId: string;
  month: MonthKey;
  talentId: string;
  brandId: string;
  budgetCents: Cents;
  extraCents: Cents;
  agencyShareCents: Cents;
  feeCents: Cents;
  managementCents: Cents;
  /** Budget + Zusatz + Gebühr */
  grossCents: Cents;
  booked: boolean;
  platforms: readonly string[];
  /** Creator-Anteil stammt aus einer vorliegenden Rechnung des Creators */
  actual: boolean;
  /**
   * Woher das Ist stammt (GELD-26): Rechnung des Creators an die Agentur (über die Agentur) oder Rechnung der Agentur
   * (Provisionsrechnung im Direktweg, Agenturrechnung bei „Nur Provision“); ohne Ist leer
   */
  actualFrom?: "creator_invoice" | "agency_invoice";
  /** Vermittlerprovision des Monats (Aufwand der Agentur, nicht im Agenturumsatz abgezogen) */
  referralCents: Cents;
  /** Agenturvergütung der Marke: um so viel zahlt die Marke weniger (grossCents ist schon danach) */
  discountCents: Cents;
  /** Monat mit Ausfallhonorar oder Minderung */
  cancellationFee?: boolean;
}

export interface Overview {
  totals: {
    openManagementCents: Cents;
    avgManagementPerMonthCents: Cents;
    currentMonth: MonthKey;
    currentMonthManagementCents: Cents;
    previousYearManagementCents: Cents;
    rebookRatePercent: number;
    rebookers: number;
    brandCount: number;
    requestedWithoutInvoice: number;
    /** Laufrate: Summe der letzten zwölf vollen Monate ÷ 12 */
    runRateCents: Cents;
    previousMonthManagementCents: Cents;
    /** Veränderung aktueller Monat zum Vormonat in Prozent, null ohne Vormonatswert */
    monthDeltaPercent: number | null;
    /** Veränderung gewählter Zeitraum zum Vorjahreszeitraum in Prozent */
    yearDeltaPercent: number | null;
    /** Gab es im Vorjahreszeitraum schon Umsatzposten? Sonst „noch kein Vergleich“ statt 0 */
    previousYearKnown: boolean;
    /** Monate, über die die Laufrate gemittelt ist (höchstens zwölf, ab dem ersten Umsatzmonat) */
    runRateMonths: number;
  };
  period: {
    budgetCents: Cents;
    feeCents: Cents;
    invoiceGrossCents: Cents;
    managementCents: Cents;
    avgDealCents: Cents;
    dealCount: number;
    budgetBookedCents: Cents;
    budgetProvisionalCents: Cents;
    feeBookedCents: Cents;
    feeOpenCents: Cents;
    extraCents: Cents;
    /** Brutto-Gesamtumsatz = Budget + Zusatz + Gebühr */
    grossCents: Cents;
    previousYearManagementCents: Cents;
    /** Vermittlerprovision im Zeitraum (Aufwand der Agentur) */
    referralCents: Cents;
  };
  months: {
    month: MonthKey;
    managementCents: Cents;
    bookedCents: Cents;
    provisionalCents: Cents;
    grossCents: Cents;
    budgetCents: Cents;
    feeCents: Cents;
    items: number;
    referralCents: Cents;
  }[];
  platforms: { platform: string; managementCents: Cents }[];
  /** Noch nicht abgerechnete Monate laufender Kooperationen je Stufe (PIPELINE_STAGES) */
  pipeline: PipelineStage[];
  talents: { talentId: string; managementCents: Cents; previousYearManagementCents: Cents; grossCents: Cents; deals: number; lastMonth: MonthKey | null }[];
  brandsLifetime: { brandId: string; managementCents: Cents; grossCents: Cents; deals: number; talents: number; lastMonth: MonthKey | null; rangeManagementCents: Cents;
    /** Nächster laufender oder geplanter Kooperationsmonat nach heute (kein Nachfassen nötig) */
    nextMonth: MonthKey | null }[];
  /** Umsatzvorschau der nächsten sechs Monate nach Leistungsmonat (netto) */
  cashflow: { month: MonthKey; grossCents: Cents; managementCents: Cents }[];
  /** Versendete, offene Rechnungen an Marken und der Agentur an Creator nach Fälligkeit (brutto, umgerechnet) */
  aging: { bucket: "not_due" | "1_7" | "8_14" | "15_30" | "30_plus"; cents: Cents; count: number }[];
  /** Freigegeben, aber noch nicht versendet (keine Forderung mit Fälligkeit) */
  toSend: { cents: Cents; count: number };
  /** Freigegebene, noch nicht versendete Stornorechnungen (keine Forderung, aber zu versenden) */
  creditNotesToSend: number;
  /** Alle Umsatz-Vorkommen (Monatsdetails) */
  occurrences: Occurrence[];
  topTalents: { talentId: string; managementCents: Cents }[];
  topBrands: { brandId: string; managementCents: Cents }[];
  /** Beträge ohne Kurs in die gewählte Währung (Umsatzposten, Rechnungen und Pipeline) */
  unconverted: number;
}

/** Ein Stufenschema für Übersicht und Kennzahlen. */
export const PIPELINE_STAGES: readonly { key: string; statuses: readonly string[] }[] = [
  { key: "planning", statuses: ["planning"] },
  { key: "contract", statuses: ["contract"] },
  { key: "production", statuses: ["concept", "production", "content_approval", "published", "reporting"] },
  { key: "invoicing", statuses: ["invoicing"] },
  { key: "blocked", statuses: ["blocked"] },
];
export interface PipelineStage {
  key: string;
  deals: number;
  /** Nettobetrag der noch nicht abgerechneten Monate in der gewählten Währung */
  budgetCents: Cents;
  /** Kooperationen ohne Geldbetrag (Gifting, Barter, Event) */
  noMoneyDeals: number;
}

/** Ohne Honorar und ohne Rechnung (0016); Barter zählt mit seinem Honorar, nur der Warenwert nie (Nachprüfung 0060) */
const NO_REVENUE_MODELS = new Set(["event", "gifting"]);
const INACTIVE_INVOICE = new Set(["draft", "cancelled", "withdrawn", "discarded", "rejected"]);
/**
 * Zählt nicht als Abrechnung oder Forderung: inaktive Rechnungen und Stornorechnungen (credit_for). Die Stornorechnung trägt
 * negative Beträge und hebt nur ihre Rechnung auf; mitgezählt verdoppelte sie die Pipeline und zog das Rechnungsvolumen ins
 * Minus (Praxistest K-149).
 */
const inactiveInvoice = (i: OverviewInvoice) => INACTIVE_INVOICE.has(i.status) || !!i.creditFor;
/** Rechnungen, deren Beträge als Ist gelten: geprüft und freigegeben (nicht eingereicht oder zur Korrektur zurückgegeben) */
const EFFECTIVE_INVOICE = new Set(["approved", "sent", "paid"]);
const BRAND_KINDS = new Set(["agency_to_brand", "talent_to_brand"]);
const AGENCY_KINDS = new Set(["agency_to_brand", "agency_to_talent"]);

const pct = (cents: Cents, percent: number): Cents => Math.round((cents * percent) / 100);

/**
 * Bruttobetrag einer Rechnung in der Zielwährung: gleiche Währung direkt, in der Agenturwährung der eingefrorene Betrag,
 * sonst der Kurs im Monat des Rechnungsdatums; null ohne Kurs.
 */
export function invoiceAmountIn(i: Pick<OverviewInvoice, "currency" | "grossCents" | "amountOrgCents" | "issueDate" | "revenueMonth">, target: string, input: Pick<OverviewInput, "today" | "orgCurrency" | "convert">): Cents | null {
  const gross = i.grossCents ?? 0;
  if (i.currency === target) return gross;
  if ((input.orgCurrency ?? null) === target && i.amountOrgCents != null) return i.amountOrgCents;
  const month = (i.issueDate ?? i.revenueMonth ?? input.today).slice(0, 7);
  return input.convert ? input.convert(gross, i.currency, target, month) : null;
}

/**
 * Offener Rest einer Rechnung (brutto abzüglich aller Zahlungen, nie unter 0) in der Zielwährung (PRAXIS-5): gleiche Währung
 * direkt, in der Agenturwährung anteilig vom eingefrorenen Betrag, sonst mit dem Kurs wie invoiceAmountIn; null ohne Kurs.
 */
export function invoiceOpenAmountIn(i: Pick<OverviewInvoice, "currency" | "grossCents" | "amountOrgCents" | "issueDate" | "revenueMonth" | "paidCents">, target: string, input: Pick<OverviewInput, "today" | "orgCurrency" | "convert">): Cents | null {
  const gross = i.grossCents ?? 0;
  const open = Math.max(0, gross - Math.max(0, i.paidCents ?? 0));
  if (open === gross) return invoiceAmountIn(i, target, input);
  if (i.currency === target) return open;
  if ((input.orgCurrency ?? null) === target && i.amountOrgCents != null) return gross ? Math.round((i.amountOrgCents * open) / gross) : 0;
  const month = (i.issueDate ?? i.revenueMonth ?? input.today).slice(0, 7);
  return input.convert ? input.convert(open, i.currency, target, month) : null;
}

/** Satz der Kooperation (Staffel geht vor, dann Kooperation → Creator → Agentur); im Nachlauf je Abrechnungsmonat (0074). */
export function dealRate(d: Pick<OverviewDeal, "commissionPercent" | "talentId" | "commissionTiers" | "postTerm">, input: Pick<OverviewInput, "talentCommissionPercent" | "agencyCommissionPercent">, month?: MonthKey | null): { percent: number; tiers: readonly CommissionTier[] | null } {
  if (month && d.postTerm) {
    const bp = dealBpForMonth({ commission_bp: null, ...d.postTerm }, month);
    if (bp != null) return { percent: bp / 100, tiers: null };
  }
  const percent = resolveCommissionRate({
    deal: d.commissionPercent,
    talent: input.talentCommissionPercent[d.talentId] ?? null,
    agency: input.agencyCommissionPercent,
  }).percent;
  return { percent, tiers: d.commissionTiers?.length ? d.commissionTiers : null };
}

/** Monate einer Kooperation, die als Umsatz zählen (KZ-01, fs59c: Blockiert nur mit verbuchten Monaten). */
export function dealOccurrences(input: OverviewInput): Occurrence[] {
  const byDeal = new Map(input.deals.map((d) => [d.id, d]));
  const orgCurrency = input.orgCurrency ?? input.currency;
  // Verbucht (eine Regel, settlement.ts): von Hand gebucht oder die Rechnung an die Marke ist bezahlt (Geld der Marke eingegangen);
  // eine als uneinbringlich ausgebuchte Rechnung steht auf „bezahlt“, das Geld kam aber nie (GELD-5)
  const paidByPeriodKind = new Set(input.invoices.filter((i) => i.status === "paid" && !i.badDebt && !i.creditFor).flatMap((i) => invoicePeriodIds(i).map((p) => `${p}:${i.kind}`)));
  // Wirksam an die Marke abgerechnet (auch über eine Sammelrechnung): zählt bei einer stornierten Kooperation weiter
  // Eine Stornorechnung rechnet nichts ab (Praxistest K-138)
  const brandBilled = new Set(input.invoices.filter((i) => BRAND_KINDS.has(i.kind) && EFFECTIVE_INVOICE.has(i.status) && !i.creditFor).flatMap((i) => invoicePeriodIds(i)));
  // Festgeschriebene Rechnung gleich welcher Seite: der Monat hat begonnen, auch wenn die Kooperation noch auf „Planung“ steht.
  // Sonst fiele er nach dem Storno der bezahlten Markenrechnung ganz aus den Kennzahlen, obwohl der Creator abgerechnet hat (K-138).
  // Ausnahme: Die Agentur zählt ausdrücklich erst ab dem unterschriebenen Vertrag (revenueByContract).
  // Praxistest K-148: Dasselbe gilt für die übrigen Monate dieser Kooperation. Ist ein Monat festgeschrieben abgerechnet, hat die
  // Kooperation begonnen; ihre weiteren Monate zählen als vorläufig, auch wenn der Arbeitsstand noch „In Planung“ heißt.
  const periodDeal = new Map(input.periods.map((p) => [p.id, p.dealId]));
  const dealBilled = new Set<string>();
  for (const i of input.invoices) {
    if (!EFFECTIVE_INVOICE.has(i.status) || i.creditFor) continue;
    if (i.dealId) dealBilled.add(i.dealId);
    for (const pid of invoicePeriodIds(i)) { const d0 = periodDeal.get(pid); if (d0) dealBilled.add(d0); }
  }
  const billedAny = new Set(input.invoices.filter((i) => EFFECTIVE_INVOICE.has(i.status) && !i.creditFor).flatMap((i) => invoicePeriodIds(i)));
  const extrasByPeriod = new Map<string, OverviewExtra[]>();
  for (const e of input.extras) {
    const key = e.periodId ?? `deal:${e.dealId}`;
    extrasByPeriod.set(key, [...(extrasByPeriod.get(key) ?? []), e]);
  }
  // Ist: freigegebene Rechnung des Creators an die Agentur je Monat (über die Agentur)
  const talentNetByPeriod = new Map<string, number>();
  // Späteste Rechnung des Creators je Monat: danach angelegte Positionen (Nachkauf) deckt sie nicht
  const talentInvoiceAt = new Map<string, string>();
  // Ist bei „Nur Provision“: wirksame Agenturrechnung des Monats (an die Marke oder an den Creator)
  const agencyNetByPeriod = new Map<string, number>();
  // Ist bei direkter Abrechnung: Provisions- oder Gebührenrechnung der Agentur an den Creator
  const agencyToTalentNet = new Map<string, number>();
  for (const i of input.invoices) {
    if (!i.periodId || i.netCents == null || !EFFECTIVE_INVOICE.has(i.status) || i.creditFor) continue;
    if (i.kind === "talent_to_agency") {
      talentNetByPeriod.set(i.periodId, (talentNetByPeriod.get(i.periodId) ?? 0) + i.netCents);
      const at = i.createdAt ?? null;
      if (at && (talentInvoiceAt.get(i.periodId) ?? "") < at) talentInvoiceAt.set(i.periodId, at);
    }
    if (AGENCY_KINDS.has(i.kind)) agencyNetByPeriod.set(i.periodId, (agencyNetByPeriod.get(i.periodId) ?? 0) + i.netCents);
    if (i.kind === "agency_to_talent") agencyToTalentNet.set(i.periodId, (agencyToTalentNet.get(i.periodId) ?? 0) + i.netCents);
  }
  const periods = [...input.periods].sort((a, b) => a.month.localeCompare(b.month));
  const fixedFeeUsed = new Set<string>();
  const fixedReferralUsed = new Set<string>();
  const firstPeriodOfDeal = new Map<string, string>();
  for (const p of periods) if (!firstPeriodOfDeal.has(p.dealId)) firstPeriodOfDeal.set(p.dealId, p.id);

  const out: Occurrence[] = [];
  for (const p of periods) {
    const d = byDeal.get(p.dealId);
    if (!d) continue;
    if (NO_REVENUE_MODELS.has(d.model) || p.cancelled) continue;
    if (input.talentId && d.talentId !== input.talentId) continue;
    const direct = d.route === "direct_to_brand";
    const booked = !!p.bookedAt || paidByPeriodKind.has(`${p.id}:${direct ? "talent_to_brand" : "agency_to_brand"}`);
    // Storniert: nur Monate mit Ausfallhonorar, verbuchte und schon an die Marke abgerechnete zählen weiter
    if (d.status === "cancelled" && !p.cancellationFee && !booked && !brandBilled.has(p.id)) continue;
    // Blockiert oder vor dem Umsatzbeginn: nur verbuchte Monate (vor dem Verbrauch der Festgebühr, sonst ginge sie verloren)
    if (d.status === "blocked" && !booked) continue;
    // Ein Ausfallhonorar ist vereinbarter Umsatz, auch vor dem Umsatzbeginn nach Arbeitsstand oder Vertrag
    if (d.revenueStarted === false && !booked && !p.cancellationFee && (d.revenueByContract || !(billedAny.has(p.id) || dealBilled.has(d.id)))) continue;

    const rate = dealRate(d, input, p.month);
    const commissionOnly = d.model === "commission_only";
    const budget = commissionOnly ? 0 : (p.amountCents ?? 0);
    const extras = [...(extrasByPeriod.get(p.id) ?? []), ...(firstPeriodOfDeal.get(d.id) === p.id ? extrasByPeriod.get(`deal:${d.id}`) ?? [] : [])];
    const extraCommissionable = extras.filter((e) => e.commissionable).reduce((a, e) => a + e.amountCents, 0);
    const passThrough = extras.filter((e) => !e.commissionable).reduce((a, e) => a + e.amountCents, 0);
    const extraTotal = extraCommissionable + passThrough;
    // Barter ohne Honorar: kein Umsatz (der Warenwert steht nie in den Beträgen)
    if (d.model === "barter" && budget === 0 && extraTotal === 0) continue;
    const discount = d.brandDiscount ?? null;
    const honorar = budget + extraCommissionable;
    const discountCents = commissionOnly ? 0 : brandDiscountCents(honorar, discount);
    // Anteil der Agentur am Honorar (Staffel, Abzug der Marke): die Marke zahlt honorar − Abzug
    const expectedShare = (base: number): number => {
      const dd = brandDiscountCents(base, discount);
      return discount?.bearer === "agency" ? commissionCents(base, rate) - dd : commissionCents(base - dd, rate);
    };

    // Agenturgebühr vor dem Anteil: die Provisionsrechnung im Direktweg kann sie enthalten (GELD-6)
    const fee = d.fee ?? input.agencyFee;
    let feeCents: number | null = 0;
    if (fee.mode === "percent") feeCents = pct(budget, fee.percent);
    else if (fee.mode === "fixed" && budget > 0 && !fixedFeeUsed.has(d.id)) {
      // Standardgebühr der Agentur ist in der Agenturwährung hinterlegt, eine Gebühr der Kooperation in deren Währung
      feeCents = d.fee || d.currency === orgCurrency ? fee.cents : input.convert ? input.convert(fee.cents, orgCurrency, d.currency, p.month) : null;
      fixedFeeUsed.add(d.id);
    }

    let agencyShare: number;
    let actual = false;
    if (commissionOnly) {
      const invoiced = agencyNetByPeriod.get(p.id);
      actual = invoiced !== undefined;
      agencyShare = invoiced ?? p.commissionForecastCents ?? 0;
    } else if (direct) {
      // Direkt an die Marke: Ist ist die Rechnung der Agentur an den Creator (Provision, ggf. mit Gebühr)
      const invoiced = agencyToTalentNet.get(p.id);
      actual = invoiced !== undefined;
      const expected = expectedShare(honorar);
      // Enthält die Provisionsrechnung die Gebühr (liegt ihr Betrag näher an Anteil + Gebühr), zählt die Gebühr nicht zusätzlich (GELD-6)
      const f = feeCents ?? 0;
      agencyShare = invoiced === undefined ? expected
        : f > 0 && Math.abs(invoiced - (expected + f)) < Math.abs(invoiced - expected) ? invoiced - f : invoiced;
      if (d.model === "fixed_plus_commission" && !actual) agencyShare += p.commissionForecastCents ?? 0;
    } else {
      const talentNet = talentNetByPeriod.get(p.id);
      actual = talentNet !== undefined;
      if (talentNet === undefined) agencyShare = expectedShare(honorar);
      else {
        // Positionen, die nach der Rechnung des Creators angelegt wurden (Nachkauf, Verlängerung), deckt sie nicht: Prognose
        const at = talentInvoiceAt.get(p.id);
        const late = (e: OverviewExtra) => !!at && !!e.createdAt && e.createdAt > at;
        const lateCommissionable = extras.filter((e) => e.commissionable && late(e)).reduce((a, e) => a + e.amountCents, 0);
        const covered = honorar - lateCommissionable;
        const coveredPassThrough = extras.filter((e) => !e.commissionable && !late(e)).reduce((a, e) => a + e.amountCents, 0);
        // Hat der Creator die durchlaufenden Posten mit abgerechnet? Maßgeblich ist, welcher Erwartung der Betrag näher liegt.
        const expectedWithout = covered - brandDiscountCents(covered, discount) - expectedShare(covered);
        const includesPassThrough = coveredPassThrough !== 0
          && Math.abs(talentNet - (expectedWithout + coveredPassThrough)) < Math.abs(talentNet - expectedWithout);
        const coveredShare = covered - brandDiscountCents(covered, discount) - (includesPassThrough ? talentNet - coveredPassThrough : talentNet);
        // Praxistest K-155: Rechnet der Creator mehr ab als vereinbart, ist der Agenturanteil negativ. Das ist ein echter Verlust
        // und bleibt sichtbar (vorher bei 0 gedeckelt: Kennzahlen zeigten „Agentur 0,00 €“ und den Creator-Anteil zu niedrig).
        agencyShare = coveredShare + (lateCommissionable ? expectedShare(honorar) - expectedShare(covered) : 0);
      }
      // Fest + Provision: die erwartete Provision gehört der Agentur zusätzlich
      if (d.model === "fixed_plus_commission") agencyShare += p.commissionForecastCents ?? 0;
    }

    // Vermittlerprovision: Prozent vom Honorar oder vom Agenturanteil je Monat, Festbetrag im ersten gezählten Monat mit Honorar
    let referral = 0;
    const ref = d.referral;
    if (ref?.mode === "percent") {
      const base = ref.base === "budget" ? budget + extraTotal - discountCents : agencyShare + (feeCents ?? 0);
      referral = Math.round((Math.max(0, base) * ref.percent) / 100);
    } else if (ref?.mode === "fixed" && budget + extraTotal > 0 && !fixedReferralUsed.has(d.id)) {
      referral = ref.cents;
      fixedReferralUsed.add(d.id);
    }

    const conv = (c: Cents): Cents | null => (d.currency === input.currency ? c : input.convert ? input.convert(c, d.currency, input.currency, p.month) : null);
    const cb = conv(budget), ce = conv(extraTotal), ca = conv(agencyShare), cf = feeCents === null ? null : conv(feeCents), cr = conv(referral), cd = conv(discountCents);
    const platforms = d.platforms ?? [];
    const base = { source: "deal" as const, dealId: d.id, periodId: p.id, month: p.month, talentId: d.talentId, brandId: d.brandId, booked, platforms, actual,
      ...(actual ? { actualFrom: commissionOnly || direct ? "agency_invoice" as const : "creator_invoice" as const } : {}),
      ...(p.cancellationFee ? { cancellationFee: true } : {}) };
    if (cb === null || ce === null || ca === null || cf === null || cr === null || cd === null) {
      out.push({ ...base, budgetCents: NaN, extraCents: NaN, agencyShareCents: NaN, feeCents: NaN, managementCents: NaN, grossCents: NaN, referralCents: NaN, discountCents: NaN });
      continue;
    }
    out.push({ ...base, budgetCents: cb, extraCents: ce, agencyShareCents: ca, feeCents: cf, managementCents: ca + cf, grossCents: cb + ce + cf - cd, referralCents: cr, discountCents: cd });
  }

  out.push(...invoiceOnlyOccurrences(input, byDeal, new Set(periods.map((p) => p.dealId))));
  out.push(...bookingOccurrences(input, new Set(out.map((o) => o.periodId))));
  return out;
}

/**
 * Kooperationen ohne Monate (Import ohne Monate, Rechnung ohne gewählten Monat, GELD-11): die wirksamen Rechnungen an die Marke
 * ohne Monat sind dann der Umsatz, im Monat der Leistung (sonst des Rechnungsdatums). Der Nettobetrag der Marke enthält eine
 * Agenturgebühr; Prozent wird herausgerechnet, ein Festbetrag zählt einmal. Der Agenturanteil kommt aus der Rechnung des
 * Creators an die Agentur (bzw. der Agentur an den Creator bei direkter Abrechnung) ohne Monat, die zu dieser Rate gehört
 * (invoiceOnlyCounterparts, GELD-18), sonst aus Satz bzw. Staffel wie im Monatspfad.
 */
function invoiceOnlyOccurrences(input: OverviewInput, byDeal: ReadonlyMap<string, OverviewDeal>, withPeriods: ReadonlySet<string>): Occurrence[] {
  const orgCurrency = input.orgCurrency ?? input.currency;
  const noPeriod = (i: OverviewInvoice) => !i.periodId && !i.periodIds?.length && !!i.dealId && !withPeriods.has(i.dealId) && EFFECTIVE_INVOICE.has(i.status) && (i.netCents ?? 0) > 0;
  const brand = new Map<string, OverviewInvoice[]>();
  const counters = new Map<string, OverviewInvoice[]>();
  for (const i of input.invoices) {
    if (!noPeriod(i)) continue;
    const d = byDeal.get(i.dealId!);
    if (!d) continue;
    const direct = d.route === "direct_to_brand";
    if (i.kind === (direct ? "talent_to_brand" : "agency_to_brand")) brand.set(d.id, [...(brand.get(d.id) ?? []), i]);
    else if (i.kind === (direct ? "agency_to_talent" : "talent_to_agency")) counters.set(d.id, [...(counters.get(d.id) ?? []), i]);
  }
  const out: Occurrence[] = [];
  for (const [dealId, list] of brand) {
    const d = byDeal.get(dealId)!;
    if (NO_REVENUE_MODELS.has(d.model) || (input.talentId && d.talentId !== input.talentId)) continue;
    const direct = d.route === "direct_to_brand";
    const commissionOnly = d.model === "commission_only";
    const fee = d.fee ?? input.agencyFee;
    let fixedLeft = fee.mode === "fixed" ? (d.fee || d.currency === orgCurrency ? fee.cents : input.convert?.(fee.cents, orgCurrency, d.currency, input.today.slice(0, 7)) ?? 0) : 0;
    const parts = [...list].sort((a, b) => invoiceMonthOf(a, input.today).localeCompare(invoiceMonthOf(b, input.today))).map((i) => {
      const net = i.netCents ?? 0;
      let feeCents = 0;
      if (!commissionOnly && fee.mode === "percent") feeCents = net - Math.round((net * 100) / (100 + fee.percent));
      else if (!commissionOnly && fee.mode === "fixed" && fixedLeft > 0) {
        feeCents = Math.min(fixedLeft, net);
        fixedLeft -= feeCents;
      }
      return { i, net, feeCents, budget: commissionOnly ? 0 : net - feeCents };
    });
    // Gegenstück je Rate: Rechnung des Creators (bzw. Provisionsrechnung) mit demselben Leistungsmonat, sonst die nächste freie Rate
    const counter = invoiceOnlyCounterparts(parts.map((p) => ({ id: p.i.id, month: invoiceMonthOf(p.i, input.today), budgetCents: p.budget })),
      (counters.get(dealId) ?? []).map((c) => ({ month: invoiceMonthOf(c, input.today), netCents: c.netCents ?? 0 })), { capByBudget: !direct });
    for (const p of parts) {
      const month = invoiceMonthOf(p.i, input.today);
      const booked = p.i.status === "paid" && !p.i.badDebt;
      if (d.status === "blocked" && !booked) continue;
      if (d.revenueStarted === false && !booked) continue;
      const matched = counter.get(p.i.id);
      let share: number;
      let actual = false;
      if (commissionOnly) {
        share = p.net;
        actual = true;
      } else if (matched !== undefined) {
        actual = true;
        share = direct ? matched : Math.max(0, p.budget - matched);
      } else {
        share = commissionCents(p.budget, dealRate(d, input, month));
      }
      const conv = (c: Cents): Cents | null => (d.currency === input.currency ? c : input.convert ? input.convert(c, d.currency, input.currency, month) : null);
      const cb = conv(p.budget), ca = conv(share), cf = conv(p.feeCents);
      const base = { source: "deal" as const, dealId: d.id, periodId: `invoice:${p.i.id}`, month, talentId: d.talentId, brandId: d.brandId, booked,
        platforms: d.platforms ?? [], actual, ...(actual ? { actualFrom: commissionOnly || direct ? "agency_invoice" as const : "creator_invoice" as const } : {}) };
      if (cb === null || ca === null || cf === null) {
        out.push({ ...base, budgetCents: NaN, extraCents: NaN, agencyShareCents: NaN, feeCents: NaN, managementCents: NaN, grossCents: NaN, referralCents: NaN, discountCents: NaN });
        continue;
      }
      out.push({ ...base, budgetCents: cb, extraCents: 0, agencyShareCents: ca, feeCents: cf, managementCents: ca + cf, grossCents: cb + cf, referralCents: 0, discountCents: 0 });
    }
  }
  return out;
}

/** Leistungsmonat einer Rechnung ohne Kooperationsmonat: Leistungsmonat, sonst Rechnungsdatum, sonst heute (YYYY-MM). */
export function invoiceMonthOf(i: Pick<OverviewInvoice, "revenueMonth" | "issueDate">, today: string): MonthKey {
  return (i.revenueMonth ?? i.issueDate ?? today).slice(0, 7);
}

/**
 * Ordnet die Gegenrechnungen einer Kooperation ohne Monate ihren Raten an die Marke zu (GELD-18): eine Rechnung des Creators
 * (bzw. Provisionsrechnung im Direktweg) gehört zur Rate mit demselben Leistungsmonat; ohne passende Rate zur ältesten Rate,
 * die noch kein Gegenstück hat. Mehrere Raten im selben Monat teilen sich den Betrag nach Budget. Über die Agentur kann der
 * Creator nie mehr bekommen als das Budget seiner Rate(n): übersteigt die Rechnung es, deckt sie die Raten ohne Gegenstück mit (eine
 * Creator-Rechnung über die ganze Kooperation). Raten ohne Gegenstück fehlen im Ergebnis; für sie gilt Satz bzw. Staffel.
 */
export function invoiceOnlyCounterparts(parts: readonly { id: string; month: MonthKey; budgetCents: Cents }[],
  counters: readonly { month: MonthKey; netCents: Cents }[], opts: { capByBudget: boolean }): Map<string, Cents> {
  const sorted = [...parts].sort((a, b) => a.month.localeCompare(b.month));
  const assigned = new Map<string, number>();
  const add = (group: readonly { id: string; budgetCents: Cents }[], cents: number) => {
    const total = group.reduce((a, p) => a + p.budgetCents, 0);
    let rest = cents;
    group.forEach((p, idx) => {
      const c = idx === group.length - 1 ? rest : Math.round(total ? (cents * p.budgetCents) / total : cents / group.length);
      rest -= c;
      assigned.set(p.id, (assigned.get(p.id) ?? 0) + c);
    });
  };
  for (const c of [...counters].sort((a, b) => a.month.localeCompare(b.month))) {
    const same = sorted.filter((p) => p.month === c.month);
    let target = same.length ? same : sorted.filter((p) => !assigned.has(p.id)).slice(0, 1);
    // Mehr als das Budget der Rate(n): die Rechnung deckt weitere Raten ohne Gegenstück mit (eine Rechnung über die ganze Kooperation)
    if (opts.capByBudget && target.length && c.netCents > target.reduce((a, p) => a + p.budgetCents, 0)) {
      target = [...target, ...sorted.filter((p) => !assigned.has(p.id) && !target.includes(p))];
    }
    // Keine freie Rate mehr: zur letzten Rate (lieber zu viel Ist als eine verschwundene Rechnung)
    if (!target.length && sorted.length) target = [sorted[sorted.length - 1]!];
    if (target.length) add(target, c.netCents);
  }
  return assigned;
}

/** Zählt eine manuelle Buchung im Umsatz? (Dieselbe Regel für Übersicht, Kennzahlen und den Buchungsdialog.) */
export function bookingCounts(b: Pick<OverviewBooking, "status" | "amountCents" | "periodId">, countedPeriods: ReadonlySet<string>): boolean {
  if (b.status === "cancelled" || b.amountCents === 0) return false;
  return !(b.periodId && countedPeriods.has(b.periodId));
}

/** Manuelle Buchungen (KZ-13): Agenturanteil wie eingefroren, Agenturgebühr ganz, Auslagen nie als Agenturanteil. */
function bookingOccurrences(input: OverviewInput, countedPeriods: ReadonlySet<string>): Occurrence[] {
  const out: Occurrence[] = [];
  const orgCurrency = input.orgCurrency ?? input.currency;
  for (const raw of input.bookings ?? []) {
    if (!bookingCounts(raw, countedPeriods)) continue;
    if (input.talentId && raw.talentId !== input.talentId) continue;
    // In der Agenturwährung zählt der beim Buchen eingefrorene Betrag (KZ-14)
    const frozen = raw.currency !== input.currency && input.currency === orgCurrency && raw.amountOrgCents != null && raw.fxRateToOrg;
    const f = frozen ? Number(raw.fxRateToOrg) : 1;
    const b = frozen ? { ...raw, currency: orgCurrency, amountCents: raw.amountOrgCents!, agencyShareCents: raw.agencyShareCents == null ? null : Math.round(raw.agencyShareCents * f) } : raw;
    const share = b.kind === "agency_fee" ? b.amountCents
      : b.kind === "expense" ? (b.agencyShareCents ?? 0)
        : (b.agencyShareCents ?? pct(b.amountCents, input.agencyCommissionPercent));
    const month = b.bookedOn.slice(0, 7);
    const conv = (c: Cents): Cents | null => (b.currency === input.currency ? c : input.convert ? input.convert(c, b.currency, input.currency, month) : null);
    const ca = conv(b.kind === "agency_fee" ? 0 : share), cf = conv(b.kind === "agency_fee" ? share : 0), cg = conv(b.amountCents);
    const bad = ca === null || cf === null || cg === null;
    out.push({
      source: "booking", dealId: b.dealId ?? `booking:${b.id}`, periodId: `booking:${b.id}`, month, talentId: b.talentId ?? "", brandId: b.brandId ?? "",
      budgetCents: bad ? NaN : b.kind === "agency_fee" ? 0 : cg!, extraCents: 0, agencyShareCents: bad ? NaN : ca!, feeCents: bad ? NaN : cf!,
      managementCents: bad ? NaN : ca! + cf!, grossCents: bad ? NaN : cg!, booked: b.status === "booked", platforms: [], actual: true, referralCents: 0, discountCents: 0,
    });
  }
  return out;
}

function sum<T>(list: readonly T[], f: (x: T) => number): number {
  return list.reduce((a, x) => a + f(x), 0);
}

/**
 * Monate, die schon in Rechnung gestellt sind (wirksame Rechnung an die Marke), von Hand gebucht wurden oder im bisherigen
 * System abgerechnet sind (übernommen, PRAXIS-R3-1).
 */
export function invoicedPeriodIds(input: Pick<OverviewInput, "invoices" | "periods">): Set<string> {
  const s = new Set(input.invoices.filter((i) => BRAND_KINDS.has(i.kind) && !inactiveInvoice(i)).flatMap((i) => invoicePeriodIds(i)));
  for (const p of input.periods) if (p.bookedAt || p.importedState) s.add(p.id);
  return s;
}

/**
 * Pipeline: laufende Kooperationen je Stufe mit dem Nettobetrag der noch nicht abgerechneten Monate (umgerechnet).
 * Modelle ohne Geld zählen als Kooperation, aber ohne Betrag.
 */
export function computePipeline(input: Pick<OverviewInput, "deals" | "periods" | "invoices" | "currency" | "convert" | "today" | "talentId"> & { brandId?: string | null }): { stages: PipelineStage[]; unconverted: number } {
  const invoiced = invoicedPeriodIds(input);
  const open = new Map<string, number>();
  const withPeriods = new Set(input.periods.map((p) => p.dealId));
  for (const p of input.periods) if (!invoiced.has(p.id) && !p.cancelled) open.set(p.dealId, (open.get(p.dealId) ?? 0) + (p.amountCents ?? 0));
  // Kooperationen ohne Monate: schon an die Marke abgerechnete Beträge (Rechnungen ohne Monat) sind nicht mehr offen (GELD-11)
  const billedWithoutPeriod = new Map<string, number>();
  for (const i of input.invoices) {
    if (!BRAND_KINDS.has(i.kind) || inactiveInvoice(i) || i.periodId || i.periodIds?.length || !i.dealId || withPeriods.has(i.dealId)) continue;
    billedWithoutPeriod.set(i.dealId, (billedWithoutPeriod.get(i.dealId) ?? 0) + (i.netCents ?? 0));
  }
  // Barter zählt mit Honorar als Geld, ohne Honorar wie Gifting
  const noMoney = (d: OverviewDeal) => NO_REVENUE_MODELS.has(d.model) || (d.model === "barter" && !(withPeriods.has(d.id) ? open.get(d.id) ?? 0 : d.budgetCents ?? 0));
  const stages: PipelineStage[] = PIPELINE_STAGES.map((s) => ({ key: s.key, deals: 0, budgetCents: 0, noMoneyDeals: 0 }));
  let unconverted = 0;
  const month = input.today.slice(0, 7);
  for (const d of input.deals) {
    if (d.archived || (input.talentId && d.talentId !== input.talentId) || (input.brandId && d.brandId !== input.brandId)) continue;
    const i = PIPELINE_STAGES.findIndex((s) => s.statuses.includes(d.status));
    if (i < 0) continue;
    const stage = stages[i]!;
    stage.deals += 1;
    if (noMoney(d)) {
      stage.noMoneyDeals += 1;
      continue;
    }
    const cents = d.model === "commission_only" ? 0 : withPeriods.has(d.id) ? open.get(d.id) ?? 0 : Math.max(0, (d.budgetCents ?? 0) - (billedWithoutPeriod.get(d.id) ?? 0));
    const conv = d.currency === input.currency ? cents : input.convert ? input.convert(cents, d.currency, input.currency, month) : null;
    if (conv === null) unconverted += 1;
    else stage.budgetCents += conv;
  }
  return { stages, unconverted };
}

export function computeOverview(input: OverviewInput): Overview {
  const all = dealOccurrences(input);
  let unconverted = all.filter((o) => Number.isNaN(o.managementCents)).length;
  const occ = all.filter((o) => !Number.isNaN(o.managementCents));
  const inRange = (m: MonthKey, from: MonthKey, to: MonthKey) => m >= from && m <= to;
  const current = input.today.slice(0, 7);
  const range = occ.filter((o) => inRange(o.month, input.from, input.to));
  const deals = new Map(input.deals.map((d) => [d.id, d]));

  const elapsed = monthRange(input.from, input.to).filter((m) => m <= current).length;
  const rangeManagement = sum(range, (o) => o.managementCents);

  // Wiederbuchungsrate über alle Zeiten: Marken mit mindestens zwei Kooperationen (fs59b #13), nur echte Kooperationen,
  // die über die Planung hinaus sind und schon begonnen haben
  const dealsPerBrand = new Map<string, Set<string>>();
  for (const o of occ) {
    // K-154 b: eine stornierte Kooperation (auch mit Ausfallhonorar) ist keine Wiederbuchung
    if (o.source !== "deal" || o.month > current || deals.get(o.dealId)?.status === "planning" || deals.get(o.dealId)?.status === "cancelled") continue;
    dealsPerBrand.set(o.brandId, (dealsPerBrand.get(o.brandId) ?? new Set()).add(o.dealId));
  }
  const brandCount = dealsPerBrand.size;
  const rebookers = [...dealsPerBrand.values()].filter((s) => s.size >= 2).length;

  // Angeforderte Monate ohne eingereichte Rechnung des Creators (an die Agentur oder direkt an die Marke)
  const talentInvoicePeriods = new Set(
    input.invoices.filter((i) => i.kind !== "agency_to_brand" && !inactiveInvoice(i) && i.periodId).map((i) => i.periodId as string),
  );
  const dealTalent = new Map(input.deals.map((d) => [d.id, d.talentId]));
  // Stornierte Monate nie; bei stornierter Kooperation nur Monate mit Ausfallhonorar
  const requestedWithoutInvoice = input.periods.filter(
    (p) => p.invoiceRequestedAt && !p.cancelled && !talentInvoicePeriods.has(p.id) && (deals.get(p.dealId)?.status !== "cancelled" || p.cancellationFee)
      && (!input.talentId || dealTalent.get(p.dealId) === input.talentId),
  ).length;

  let invoiceGross = 0;
  for (const i of input.invoices) {
    if (!BRAND_KINDS.has(i.kind) || inactiveInvoice(i) || !i.revenueMonth || !inRange(i.revenueMonth.slice(0, 7), input.from, input.to)) continue;
    if (input.talentId && i.talentId !== input.talentId) continue;
    const c = invoiceAmountIn(i, input.currency, input);
    if (c === null) unconverted += 1;
    else invoiceGross += c;
  }

  const rangeDeals = range.filter((o) => o.source === "deal");
  const dealsInRange = new Set(rangeDeals.map((o) => o.dealId));
  const budgetAndFees = sum(rangeDeals, (o) => o.budgetCents + o.extraCents + o.feeCents);

  const months = monthRange(input.from, input.to).map((m) => {
    const list = range.filter((o) => o.month === m);
    return {
      month: m,
      managementCents: sum(list, (o) => o.managementCents),
      bookedCents: sum(list.filter((o) => o.booked), (o) => o.managementCents),
      provisionalCents: sum(list.filter((o) => !o.booked), (o) => o.managementCents),
      grossCents: sum(list, (o) => o.grossCents),
      budgetCents: sum(list, (o) => o.budgetCents + o.extraCents),
      feeCents: sum(list, (o) => o.feeCents),
      items: list.length,
      referralCents: sum(list, (o) => o.referralCents),
    };
  });

  const top = (key: "talentId" | "brandId") => {
    const m = new Map<string, number>();
    for (const o of range) m.set(o[key], (m.get(o[key]) ?? 0) + o.managementCents);
    return [...m.entries()].sort((a, b) => b[1] - a[1]);
  };

  const prevMonth = addMonths(current, -1);
  const prevMonthMgmt = sum(occ.filter((o) => o.month === prevMonth), (o) => o.managementCents);
  const curMonthMgmt = sum(occ.filter((o) => o.month === current), (o) => o.managementCents);
  // Laufrate: Mittel der letzten vollen Monate, höchstens zwölf, erst ab dem ersten Umsatzmonat
  const firstMonth = occ.reduce<string | null>((a, o) => (!a || o.month < a ? o.month : a), null);
  const runFrom = firstMonth && firstMonth > addMonths(current, -12) ? firstMonth : addMonths(current, -12);
  const runRateMonths = firstMonth && runFrom <= prevMonth ? monthRange(runFrom, prevMonth).length : 0;
  const runRate = runRateMonths ? Math.round(sum(occ.filter((o) => o.month >= runFrom && o.month <= prevMonth), (o) => o.managementCents) / runRateMonths) : 0;
  const pyFrom = addMonths(input.from, -12), pyTo = addMonths(input.to, -12);
  const previousYear = sum(occ.filter((o) => inRange(o.month, pyFrom, pyTo)), (o) => o.managementCents);
  const previousYearKnown = !!firstMonth && firstMonth <= pyTo;
  const delta = (now: number, before: number): number | null => (before > 0 ? ((now - before) / before) * 100 : null);

  const platformMap = new Map<string, number>();
  for (const o of range) {
    const list = o.platforms.length ? o.platforms : [o.source === "booking" ? "manual" : "other"];
    for (const p of list) platformMap.set(p, (platformMap.get(p) ?? 0) + o.managementCents / list.length);
  }

  const pipe = computePipeline(input);
  unconverted += pipe.unconverted;

  const talentMap = new Map<string, { managementCents: number; previousYearManagementCents: number; grossCents: number; deals: Set<string>; lastMonth: string | null }>();
  const tEntry = (id: string) => {
    let e = talentMap.get(id);
    if (!e) talentMap.set(id, (e = { managementCents: 0, previousYearManagementCents: 0, grossCents: 0, deals: new Set(), lastMonth: null }));
    return e;
  };
  for (const o of occ) {
    if (!o.talentId) continue;
    if (inRange(o.month, input.from, input.to)) {
      const e = tEntry(o.talentId);
      e.managementCents += o.managementCents;
      e.grossCents += o.grossCents;
      if (o.source === "deal") e.deals.add(o.dealId);
      if (!e.lastMonth || o.month > e.lastMonth) e.lastMonth = o.month;
    } else if (inRange(o.month, pyFrom, pyTo)) {
      tEntry(o.talentId).previousYearManagementCents += o.managementCents;
    }
  }

  const brandMap = new Map<string, { managementCents: number; grossCents: number; deals: Set<string>; talents: Set<string>; lastMonth: string | null; rangeManagementCents: number; nextMonth: string | null }>();
  const bEntry = (id: string) => {
    let e = brandMap.get(id);
    if (!e) brandMap.set(id, (e = { managementCents: 0, grossCents: 0, deals: new Set(), talents: new Set(), lastMonth: null, rangeManagementCents: 0, nextMonth: null }));
    return e;
  };
  for (const o of occ) {
    if (!o.brandId) continue;
    if (o.month > current) continue;
    const e = bEntry(o.brandId);
    e.managementCents += o.managementCents;
    e.grossCents += o.grossCents;
    if (o.source === "deal") e.deals.add(o.dealId);
    if (o.talentId) e.talents.add(o.talentId);
    if (!e.lastMonth || o.month > e.lastMonth) e.lastMonth = o.month;
    if (inRange(o.month, input.from, input.to)) e.rangeManagementCents += o.managementCents;
  }
  // Nachfassen erübrigt sich, wenn eine Kooperation läuft oder geplant ist (auch Monate ohne Umsatz, z. B. Gifting)
  for (const p of input.periods) {
    const d = deals.get(p.dealId);
    if (!d || d.status === "cancelled" || d.archived || p.cancelled || p.month.slice(0, 7) <= current) continue;
    const e = brandMap.get(d.brandId);
    if (e && (!e.nextMonth || p.month < e.nextMonth)) e.nextMonth = p.month.slice(0, 7);
  }

  const cashflow = monthRange(addMonths(current, 1), addMonths(current, 6)).map((m) => {
    const list = occ.filter((o) => o.month === m);
    return { month: m, grossCents: sum(list, (o) => o.grossCents), managementCents: sum(list, (o) => o.managementCents) };
  });

  const agingBuckets = { not_due: [0, 0], "1_7": [0, 0], "8_14": [0, 0], "15_30": [0, 0], "30_plus": [0, 0] } as Record<"not_due" | "1_7" | "8_14" | "15_30" | "30_plus", [number, number]>;
  const toSend = { cents: 0, count: 0 };
  let creditNotesToSend = 0;
  for (const i of input.invoices) {
    // Offene Posten: Rechnungen an Marken (gleich wer sie stellt) und Rechnungen der Agentur an Creator, nur der offene Rest
    // nach Teilzahlungen (PRAXIS-5, wie Rechnungsliste und Liquidität)
    if (i.kind === "talent_to_agency" || !["approved", "sent"].includes(i.status)) continue;
    if (input.talentId && i.talentId !== input.talentId) continue;
    // Stornorechnungen sind keine Forderung (K-151): eigens zählen, was davon noch nicht versendet ist
    if (i.creditFor) {
      if (i.status === "approved") creditNotesToSend += 1;
      continue;
    }
    if ((i.grossCents ?? 0) > 0 && (i.paidCents ?? 0) >= (i.grossCents ?? 0)) continue;
    const c = invoiceOpenAmountIn(i, input.currency, input);
    if (c === null) {
      unconverted += 1;
      continue;
    }
    if (i.status === "approved") {
      toSend.cents += c;
      toSend.count += 1;
      continue;
    }
    // Nicht zugegangen: keine Frist, also „noch nicht fällig“ statt überfällig (GELD-R3-7)
    const days = i.dueDate && !i.awaitsDelivery ? Math.round((Date.parse(`${input.today}T00:00:00Z`) - Date.parse(`${i.dueDate}T00:00:00Z`)) / 86_400_000) : 0;
    const key = days <= 0 ? "not_due" : days <= 7 ? "1_7" : days <= 14 ? "8_14" : days <= 30 ? "15_30" : "30_plus";
    agingBuckets[key][0] += c;
    agingBuckets[key][1] += 1;
  }

  return {
    totals: {
      openManagementCents: sum(occ.filter((o) => !o.booked), (o) => o.managementCents),
      avgManagementPerMonthCents: elapsed ? Math.round(sum(range.filter((o) => o.month <= current), (o) => o.managementCents) / elapsed) : 0,
      currentMonth: current,
      currentMonthManagementCents: curMonthMgmt,
      previousYearManagementCents: previousYear,
      rebookRatePercent: brandCount ? (rebookers / brandCount) * 100 : 0,
      rebookers,
      brandCount,
      requestedWithoutInvoice,
      runRateCents: runRate,
      runRateMonths,
      previousMonthManagementCents: prevMonthMgmt,
      monthDeltaPercent: delta(curMonthMgmt, prevMonthMgmt),
      yearDeltaPercent: previousYearKnown ? delta(rangeManagement, previousYear) : null,
      previousYearKnown,
    },
    period: {
      budgetCents: sum(range, (o) => o.budgetCents + o.extraCents),
      feeCents: sum(range, (o) => o.feeCents),
      invoiceGrossCents: invoiceGross,
      managementCents: rangeManagement,
      avgDealCents: dealsInRange.size ? Math.round(budgetAndFees / dealsInRange.size) : 0,
      dealCount: dealsInRange.size,
      budgetBookedCents: sum(range.filter((o) => o.booked), (o) => o.budgetCents + o.extraCents),
      budgetProvisionalCents: sum(range.filter((o) => !o.booked), (o) => o.budgetCents + o.extraCents),
      feeBookedCents: sum(range.filter((o) => o.booked), (o) => o.feeCents),
      feeOpenCents: sum(range.filter((o) => !o.booked), (o) => o.feeCents),
      extraCents: sum(range, (o) => o.extraCents),
      grossCents: sum(range, (o) => o.grossCents),
      previousYearManagementCents: previousYear,
      referralCents: sum(range, (o) => o.referralCents),
    },
    months,
    topTalents: top("talentId").filter(([id]) => id).map(([talentId, managementCents]) => ({ talentId, managementCents })),
    topBrands: top("brandId").filter(([id]) => id).map(([brandId, managementCents]) => ({ brandId, managementCents })),
    unconverted,
    platforms: [...platformMap.entries()].sort((a, b) => b[1] - a[1]).map(([platform, v]) => ({ platform, managementCents: Math.round(v) })),
    pipeline: pipe.stages,
    talents: [...talentMap.entries()]
      .map(([talentId, e]) => ({ talentId, managementCents: e.managementCents, previousYearManagementCents: e.previousYearManagementCents, grossCents: e.grossCents, deals: e.deals.size, lastMonth: e.lastMonth }))
      .sort((a, b) => b.managementCents - a.managementCents),
    brandsLifetime: [...brandMap.entries()]
      .map(([brandId, e]) => ({ brandId, managementCents: e.managementCents, grossCents: e.grossCents, deals: e.deals.size, talents: e.talents.size, lastMonth: e.lastMonth,
        rangeManagementCents: e.rangeManagementCents, nextMonth: e.nextMonth }))
      .sort((a, b) => b.managementCents - a.managementCents),
    cashflow,
    aging: (["not_due", "1_7", "8_14", "15_30", "30_plus"] as const).map((bucket) => ({ bucket, cents: agingBuckets[bucket][0], count: agingBuckets[bucket][1] })),
    toSend,
    creditNotesToSend,
    occurrences: occ,
  };
}
